import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Row, Col, Button } from "react-bootstrap";
import Loader from "../../../../components/loader/index.js";
import axiosClient from "../../../../config/axios.js";
import qs from "qs";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Circle,
  Polyline,
} from "@react-google-maps/api";
import BasePointSelector from "./BasePointSelector";
import NearbyOrdersList from "./NearbyOrdersList";
import ModalSkeleton from "./ModalSkeleton";
import MapFilters from "./MapFilters";

const ModalOrdersMap = ({ values, modalOrderValues, modalInstance }) => {
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [nearbyPoints, setNearbyPoints] = useState([]);
  const [radius, setRadius] = useState(5000); // 10km por defecto
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showAllRadii, setShowAllRadii] = useState(false);
  const [extendedSearch, setExtendedSearch] = useState(null); // Estado para búsqueda extendida de 100 km
  const [filters, setFilters] = useState({
    craneTypes: [],
    vehicleTypeId: null,
    clientTypeId: null,
    insuranceId: null,
  });
  const excludedOrderIds = useRef([]);
  const hasAutoSelectedFirstPoint = useRef(false);
  const handleFetchNearbyPointsRef = useRef(null);
  const lastFetchedOrderId = useRef(null);
  const lastFetchedLocations = useRef(null);
  const directionsCache = useRef({});
  // Cache para guardar los datos de todos los radios por punto base
  // Estructura: { "lat-lng": { allRadii: [...], radius5000: [...], radius10000: [...], radius15000: [...] } }
  const cachedPointData = useRef({});
  const mapRef = useRef(null); // Referencia al mapa de Google Maps
  // console.log("values", values);

  // Limpiar datos cuando cambia la orden o las ubicaciones
  useEffect(() => {
    const currentOrderId = values?.id;
    const currentLocations = JSON.stringify(values?.order_locations);

    // Si cambió la orden o las ubicaciones, limpiar los datos
    if (
      (lastFetchedOrderId.current !== null &&
        lastFetchedOrderId.current !== currentOrderId) ||
      (lastFetchedLocations.current !== null &&
        lastFetchedLocations.current !== currentLocations)
    ) {
      hasAutoSelectedFirstPoint.current = false;
      setSelectedPoint(null);
      setNearbyPoints([]);
      setSelectedOrderId(null);
      setExtendedSearch(null); // Limpiar búsqueda extendida
      excludedOrderIds.current = [];
      lastFetchedOrderId.current = null;
      lastFetchedLocations.current = null;
      cachedPointData.current = {}; // Limpiar caché
    }
  }, [values?.id, values?.order_locations]);

  // Guardar referencias cuando se cargan datos exitosamente
  useEffect(() => {
    // Si hay datos cargados y un punto seleccionado, guardar las referencias
    // Solo si las referencias no están guardadas o son diferentes (para actualizarlas)
    if (nearbyPoints.length > 0 && selectedPoint !== null) {
      const currentOrderId = values?.id;
      const currentLocations = JSON.stringify(values?.order_locations);

      // Solo actualizar si las referencias son diferentes o no están guardadas
      if (
        lastFetchedOrderId.current !== currentOrderId ||
        lastFetchedLocations.current !== currentLocations
      ) {
        lastFetchedOrderId.current = currentOrderId;
        lastFetchedLocations.current = currentLocations;
      }
    }
  }, [nearbyPoints.length, selectedPoint, values?.id, values?.order_locations]);

  // Cargar datos al abrir modal
  useEffect(() => {
    const modalEl = modalOrderValues.current;
    if (!modalEl) return;

    const handleShown = () => {
      setLoading(false);
    };

    modalEl.addEventListener("shown.bs.modal", handleShown);
    return () => modalEl.removeEventListener("shown.bs.modal", handleShown);
  }, []);

  // Inicializar filtros con valores por defecto
  useEffect(() => {
    if (values && (!filters.craneTypes || filters.craneTypes.length === 0)) {
      const craneTypeNames = values?.crane_id?.crane_type_name;
      const defaultCraneTypes = craneTypeNames
        ? craneTypeNames
            .split(", ")
            .filter(Boolean)
            .map((name) => ({
              value: name,
              label: name,
            }))
        : [];

      const defaultVehicleType =
        values?.vehiculos?.[0]?.vehicle_type_id || null;
      const defaultClientType = values?.client_type_id || null;
      const defaultInsurance = values?.insurance_id || null;

      setFilters({
        craneTypes: defaultCraneTypes,
        vehicleTypeId: defaultVehicleType,
        clientTypeId: defaultClientType,
        insuranceId: defaultInsurance,
      });
    }
  }, [values]);

  // Seleccionar automáticamente el primer punto base al abrir el modal
  useEffect(() => {
    const modalEl = modalOrderValues.current;
    if (!modalEl) return;

    const handleShown = () => {
      // Verificar si los datos ya fueron cargados para esta orden
      const currentOrderId = values?.id;
      const currentLocations = JSON.stringify(values?.order_locations);
      const hasDataCached =
        currentOrderId === lastFetchedOrderId.current &&
        currentLocations === lastFetchedLocations.current &&
        nearbyPoints.length > 0 &&
        selectedPoint !== null;

      if (hasDataCached) {
        // Ya hay datos cargados, solo mostrar el estado anterior sin hacer fetch
        return;
      }

      // Seleccionar automáticamente el primer punto base solo si no hay datos cargados
      if (
        !hasAutoSelectedFirstPoint.current &&
        values?.order_locations &&
        values.order_locations.length > 0
      ) {
        const firstLocation = values.order_locations[0];
        // Verificar que tenga coordenadas válidas
        if (firstLocation.latitude && firstLocation.longitude) {
          hasAutoSelectedFirstPoint.current = true;
          // Usar setTimeout para asegurar que el modal esté completamente renderizado
          setTimeout(() => {
            setSelectedPoint(firstLocation);
            // Al entrar al modal, ejecutar fetchAllRadii para cargar todos los datos de una vez
            setShowAllRadii(true);
            fetchAllRadii(firstLocation);
          }, 100);
        }
      }
    };

    const handleHidden = () => {
      // NO limpiar los datos cuando se cierra el modal para mantener el estado
      // Resetear el flag de selección automática para permitir selección en la próxima apertura
      // si no hay datos en caché
      hasAutoSelectedFirstPoint.current = false;
    };

    modalEl.addEventListener("shown.bs.modal", handleShown);
    modalEl.addEventListener("hidden.bs.modal", handleHidden);
    return () => {
      modalEl.removeEventListener("shown.bs.modal", handleShown);
      modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [values?.order_locations, values?.id, nearbyPoints.length, selectedPoint]);

  // Verificar cuando Google Maps está cargado
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
      }
    };

    // Verificar inmediatamente
    checkGoogleMaps();

    // Verificar periódicamente hasta que esté cargado
    const interval = setInterval(() => {
      if (window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    // Limpiar intervalo después de 10 segundos
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Función helper para obtener el min_radius basado en el radius
  const getMinRadius = useCallback((radiusValue) => {
    if (radiusValue <= 5000) return 1; // 1 a 5000
    if (radiusValue <= 10000) return 5001; // 5001 a 10000
    if (radiusValue <= 15000) return 10001; // 10001 a 15000
    return 0; // Por defecto
  }, []);

  // Función helper para obtener la clave del caché de un punto
  const getPointCacheKey = useCallback((location) => {
    if (!location || !location.latitude || !location.longitude) return null;
    return `${Number(location.latitude).toFixed(6)}-${Number(
      location.longitude
    ).toFixed(6)}`;
  }, []);

  const fetchNearbyPoints = useCallback(
    async (
      location,
      customRadius = null,
      customMinRadius = null,
      excludedIds = null,
      customFilters = null
    ) => {
      if (!location) return [];
      try {
        // Usar filtros personalizados o los del estado
        const filtersToUse = customFilters || filters;

        // Extraer tipos desde los filtros
        const tipos = filtersToUse.craneTypes
          ? filtersToUse.craneTypes.map((ct) => ct.value)
          : [];

        const radiusToUse = customRadius || radius;
        const minRadiusToUse =
          customMinRadius !== null
            ? customMinRadius
            : getMinRadius(radiusToUse);
        const excludedToUse =
          excludedIds !== null ? excludedIds : excludedOrderIds.current;

        const response = await axiosClient.get(`/locations/nearbyMap`, {
          params: {
            longitude: Number(location.longitude),
            latitude: Number(location.latitude),
            radius: radiusToUse,
            min_radius: minRadiusToUse,
            tipos, // array limpio: ["TIPO A", "TIPO B"]
            order_id: values?.id,
            excluded_ids: excludedToUse,
            insurance_id: filtersToUse.insuranceId?.value || null,
            client_type_id: filtersToUse.clientTypeId?.value || null,
            vehicle_type_id: filtersToUse.vehicleTypeId?.value || null,
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
        });

        const data = response.data?.data || [];

        // Actualizar excluded_ids con los nuevos order_ids encontrados
        data.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        return data;
      } catch (err) {
        console.error("Error al obtener puntos cercanos:", err);
        return [];
      }
    },
    [radius, values, getMinRadius, filters]
  );

  const fetchAllRadii = useCallback(
    async (location) => {
      if (!location) return;

      const cacheKey = getPointCacheKey(location);
      if (!cacheKey) return;

      // Verificar si ya tenemos datos en caché para este punto
      if (cachedPointData.current[cacheKey]?.allRadii) {
        const cachedData = cachedPointData.current[cacheKey];
        const allResults = [];
        cachedData.allRadii.forEach((batch) => {
          allResults.push(...batch.data);
        });

        // Restaurar búsqueda extendida del caché si existe
        if (cachedData.extendedSearch) {
          setExtendedSearch(cachedData.extendedSearch);
          // Agregar resultados de búsqueda extendida si existen
          if (
            cachedData.extendedSearch.data &&
            cachedData.extendedSearch.data.length > 0
          ) {
            allResults.push(...cachedData.extendedSearch.data);
          }
        } else {
          setExtendedSearch(null);
        }

        // Eliminar duplicados por order_id
        const uniqueResults = [];
        const seenOrderIds = new Set();
        allResults.forEach((item) => {
          if (!seenOrderIds.has(item.order_id)) {
            seenOrderIds.add(item.order_id);
            uniqueResults.push(item);
          }
        });

        // Actualizar excluded_ids
        uniqueResults.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(uniqueResults);
        setSelectedOrderId(null);
        setShowAllRadii(true);
        return;
      }

      setLoading(true);
      setShowAllRadii(true);
      excludedOrderIds.current = []; // Resetear excluded_ids

      try {
        // Extraer tipos desde los filtros
        const tipos = filters.craneTypes
          ? filters.craneTypes.map((ct) => ct.value)
          : [];

        // Llamar al nuevo endpoint batch
        const response = await axiosClient.post(`/locations/nearbyMapBatch`, {
          longitude: Number(location.longitude),
          latitude: Number(location.latitude),
          radius_configs: [
            { radius: 5000, min_radius: 1 },
            { radius: 10000, min_radius: 5001 },
            { radius: 15000, min_radius: 10001 },
          ],
          tipos,
          order_id: values?.id,
          excluded_ids: [],
          insurance_id: filters.insuranceId?.value || null,
          client_type_id: filters.clientTypeId?.value || null,
          vehicle_type_id: filters.vehicleTypeId?.value || null,
        });

        const batchResults = response.data?.data || [];
        const extendedSearchData = response.data?.extendedSearch || null;

        // Guardar la información de búsqueda extendida
        setExtendedSearch(extendedSearchData);

        // Guardar los datos en caché por punto base
        if (!cachedPointData.current[cacheKey]) {
          cachedPointData.current[cacheKey] = {};
        }
        cachedPointData.current[cacheKey].allRadii = batchResults;
        // Guardar también la búsqueda extendida en el caché
        if (extendedSearchData) {
          cachedPointData.current[cacheKey].extendedSearch = extendedSearchData;
        }

        // También guardar datos individuales por radio para acceso rápido
        batchResults.forEach((batch) => {
          if (batch.radius === 5000 && batch.min_radius === 1) {
            cachedPointData.current[cacheKey].radius5000 = batch.data;
          } else if (batch.radius === 10000 && batch.min_radius === 5001) {
            cachedPointData.current[cacheKey].radius10000 = batch.data;
          } else if (batch.radius === 15000 && batch.min_radius === 10001) {
            cachedPointData.current[cacheKey].radius15000 = batch.data;
          }
        });

        // Combinar todos los resultados (incluyendo búsqueda extendida si existe)
        const allResults = [];
        batchResults.forEach((batch) => {
          allResults.push(...batch.data);
        });

        // Agregar resultados de búsqueda extendida si existe
        if (
          extendedSearchData &&
          extendedSearchData.data &&
          extendedSearchData.data.length > 0
        ) {
          allResults.push(...extendedSearchData.data);
        }

        // Eliminar duplicados por order_id
        const uniqueResults = [];
        const seenOrderIds = new Set();
        allResults.forEach((item) => {
          if (!seenOrderIds.has(item.order_id)) {
            seenOrderIds.add(item.order_id);
            uniqueResults.push(item);
          }
        });

        // Actualizar excluded_ids con los nuevos order_ids encontrados
        uniqueResults.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(uniqueResults);
        setSelectedOrderId(null);
      } catch (err) {
        console.error("Error al obtener puntos de todos los radios:", err);
      } finally {
        setLoading(false);
      }
    },
    [values, getPointCacheKey, filters]
  );

  const handleFetchNearbyPoints = useCallback(
    async (location) => {
      if (!location) return;

      const cacheKey = getPointCacheKey(location);
      if (!cacheKey) return;

      // Verificar si ya tenemos datos en caché para este punto y radio
      const cacheKeyForRadius = `radius${radius}`;
      if (cachedPointData.current[cacheKey]?.[cacheKeyForRadius]) {
        const cachedData = cachedPointData.current[cacheKey][cacheKeyForRadius];

        // Actualizar excluded_ids
        cachedData.forEach((item) => {
          if (!excludedOrderIds.current.includes(item.order_id)) {
            excludedOrderIds.current.push(item.order_id);
          }
        });

        setNearbyPoints(cachedData);
        setSelectedOrderId(null);
        setShowAllRadii(false);
        return;
      }

      setLoading(true);
      setShowAllRadii(false);
      excludedOrderIds.current = []; // Resetear excluded_ids

      try {
        const data = await fetchNearbyPoints(
          location,
          null,
          null,
          null,
          filters
        );

        // Guardar en caché
        if (!cachedPointData.current[cacheKey]) {
          cachedPointData.current[cacheKey] = {};
        }
        cachedPointData.current[cacheKey][cacheKeyForRadius] = data;

        setNearbyPoints(data);
        setSelectedOrderId(null);
      } finally {
        setLoading(false);
      }
    },
    [fetchNearbyPoints, radius, getPointCacheKey, filters]
  );

  // Actualizar la referencia de handleFetchNearbyPoints
  useEffect(() => {
    handleFetchNearbyPointsRef.current = handleFetchNearbyPoints;
  }, [handleFetchNearbyPoints]);

  const handleSelectPoint = (location) => {
    setSelectedPoint(location);
    // No hacer fetch aquí, el useEffect se encargará de cargar los datos
    // Esto permite que use el caché si está disponible
  };

  // Función para manejar la búsqueda desde los filtros
  const handleSearch = useCallback(
    (searchFilters) => {
      // Limpiar caché cuando cambian los filtros
      cachedPointData.current = {};
      excludedOrderIds.current = [];
      setNearbyPoints([]);
      setSelectedOrderId(null);
      setExtendedSearch(null); // Limpiar búsqueda extendida

      // Si hay un punto seleccionado, recargar los datos con los nuevos filtros
      if (selectedPoint) {
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    },
    [selectedPoint, fetchAllRadii]
  );

  // No limpiar automáticamente el caché cuando cambian los filtros
  // Solo se limpiará cuando el usuario haga clic en "Buscar"

  // Cargar datos cuando cambia el punto base seleccionado
  useEffect(() => {
    if (selectedPoint) {
      const cacheKey = getPointCacheKey(selectedPoint);

      // Siempre intentar cargar todos los radios primero (como con el primer punto)
      // Si ya hay datos en caché, usarlos; si no, hacer fetchAllRadii
      if (cacheKey && cachedPointData.current[cacheKey]?.allRadii) {
        // Ya hay datos en caché, usar los datos según el modo actual
        const cachedData = cachedPointData.current[cacheKey];

        if (!showAllRadii) {
          // Modo radio específico - extraer datos del radio específico desde allRadii
          const cacheKeyForRadius = `radius${radius}`;
          let cachedDataForRadius = cachedData[cacheKeyForRadius];

          // Si no está guardado individualmente, extraerlo de allRadii
          if (!cachedDataForRadius) {
            const batch = cachedData.allRadii.find(
              (b) =>
                b.radius === radius && b.min_radius === getMinRadius(radius)
            );
            if (batch) {
              cachedDataForRadius = batch.data;
              // Guardar para acceso rápido futuro
              cachedData[cacheKeyForRadius] = cachedDataForRadius;
            }
          }

          if (cachedDataForRadius) {
            // Actualizar excluded_ids
            cachedDataForRadius.forEach((item) => {
              if (!excludedOrderIds.current.includes(item.order_id)) {
                excludedOrderIds.current.push(item.order_id);
              }
            });

            setNearbyPoints(cachedDataForRadius);
            setSelectedOrderId(null);
            setShowAllRadii(false);
            return;
          }
        } else {
          // Modo "Todos"
          const allResults = [];
          cachedData.allRadii.forEach((batch) => {
            allResults.push(...batch.data);
          });

          // Restaurar y agregar datos de búsqueda extendida si existen
          if (cachedData.extendedSearch) {
            setExtendedSearch(cachedData.extendedSearch);
            if (
              cachedData.extendedSearch.data &&
              cachedData.extendedSearch.data.length > 0
            ) {
              allResults.push(...cachedData.extendedSearch.data);
            }
          } else {
            setExtendedSearch(null);
          }

          // Eliminar duplicados por order_id
          const uniqueResults = [];
          const seenOrderIds = new Set();
          allResults.forEach((item) => {
            if (!seenOrderIds.has(item.order_id)) {
              seenOrderIds.add(item.order_id);
              uniqueResults.push(item);
            }
          });

          // Actualizar excluded_ids
          uniqueResults.forEach((item) => {
            if (!excludedOrderIds.current.includes(item.order_id)) {
              excludedOrderIds.current.push(item.order_id);
            }
          });

          setNearbyPoints(uniqueResults);
          setSelectedOrderId(null);
          setShowAllRadii(true);
          return;
        }
      } else {
        // No hay datos en caché, hacer fetchAllRadii para cargar todos los datos de una vez
        // Esto asegura que todos los radios estén disponibles en caché
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoint]);

  // Actualizar puntos cuando cambia el radio o el modo (Todos/radio específico)
  useEffect(() => {
    if (selectedPoint) {
      const cacheKey = getPointCacheKey(selectedPoint);

      // Siempre usar datos del caché si están disponibles
      if (cacheKey && cachedPointData.current[cacheKey]?.allRadii) {
        const cachedData = cachedPointData.current[cacheKey];

        if (!showAllRadii) {
          // Modo radio específico - extraer datos del radio específico desde allRadii
          const cacheKeyForRadius = `radius${radius}`;
          let cachedDataForRadius = cachedData[cacheKeyForRadius];

          // Si no está guardado individualmente, extraerlo de allRadii
          if (!cachedDataForRadius) {
            const batch = cachedData.allRadii.find(
              (b) =>
                b.radius === radius && b.min_radius === getMinRadius(radius)
            );
            if (batch) {
              cachedDataForRadius = batch.data;
              // Guardar para acceso rápido futuro
              cachedData[cacheKeyForRadius] = cachedDataForRadius;
            }
          }

          if (cachedDataForRadius) {
            // Actualizar excluded_ids
            cachedDataForRadius.forEach((item) => {
              if (!excludedOrderIds.current.includes(item.order_id)) {
                excludedOrderIds.current.push(item.order_id);
              }
            });

            setNearbyPoints(cachedDataForRadius);
            setSelectedOrderId(null);
            return;
          }
        } else {
          // Modo "Todos"
          const allResults = [];
          cachedData.allRadii.forEach((batch) => {
            allResults.push(...batch.data);
          });

          // Restaurar y agregar datos de búsqueda extendida si existen
          if (cachedData.extendedSearch) {
            setExtendedSearch(cachedData.extendedSearch);
            if (
              cachedData.extendedSearch.data &&
              cachedData.extendedSearch.data.length > 0
            ) {
              allResults.push(...cachedData.extendedSearch.data);
            }
          } else {
            setExtendedSearch(null);
          }

          // Eliminar duplicados por order_id
          const uniqueResults = [];
          const seenOrderIds = new Set();
          allResults.forEach((item) => {
            if (!seenOrderIds.has(item.order_id)) {
              seenOrderIds.add(item.order_id);
              uniqueResults.push(item);
            }
          });

          // Actualizar excluded_ids
          uniqueResults.forEach((item) => {
            if (!excludedOrderIds.current.includes(item.order_id)) {
              excludedOrderIds.current.push(item.order_id);
            }
          });

          setNearbyPoints(uniqueResults);
          setSelectedOrderId(null);
          return;
        }
      }

      // Si no hay datos en caché, esto no debería pasar si fetchAllRadii se ejecutó correctamente
      // Pero por seguridad, si no hay datos, hacer fetchAllRadii
      if (!cacheKey || !cachedPointData.current[cacheKey]?.allRadii) {
        setShowAllRadii(true);
        fetchAllRadii(selectedPoint);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, showAllRadii]);

  const center = selectedPoint
    ? {
        lat: Number(selectedPoint.latitude),
        lng: Number(selectedPoint.longitude),
      }
    : {
        lat: Number(values?.order_locations?.[0]?.latitude) || 17.05,
        lng: Number(values?.order_locations?.[0]?.longitude) || -96.72,
      };

  // Ajustar la vista del mapa cuando hay un círculo extendido
  useEffect(() => {
    if (
      mapRef.current &&
      selectedPoint &&
      extendedSearch &&
      extendedSearch.radius &&
      isGoogleMapsLoaded &&
      window.google &&
      window.google.maps
    ) {
      // Usar setTimeout para asegurar que el círculo se haya renderizado
      const timeoutId = setTimeout(() => {
        if (mapRef.current) {
          // Crear bounds que incluyan el círculo extendido
          const bounds = new window.google.maps.LatLngBounds();
          const centerLat = Number(selectedPoint.latitude);
          const centerLng = Number(selectedPoint.longitude);

          // Calcular los puntos del círculo extendido para incluirlos en los bounds
          // Usar un círculo aproximado con 8 puntos para crear los bounds
          const radiusInMeters = extendedSearch.radius;
          const radiusInDegrees = radiusInMeters / 111320; // Aproximación: 1 grado ≈ 111320 metros

          // Agregar el centro
          bounds.extend({ lat: centerLat, lng: centerLng });

          // Agregar puntos alrededor del círculo para asegurar que esté visible
          for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI * 2) / 8;
            const lat = centerLat + radiusInDegrees * Math.cos(angle);
            const lng =
              centerLng +
              (radiusInDegrees * Math.sin(angle)) /
                Math.cos((centerLat * Math.PI) / 180);
            bounds.extend({ lat, lng });
          }

          // Ajustar la vista para incluir el círculo extendido con un pequeño padding
          mapRef.current.fitBounds(bounds, { padding: 20 });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedPoint, extendedSearch, isGoogleMapsLoaded]);

  // Calcular el índice del punto base seleccionado
  const selectedPointIndex = useMemo(() => {
    if (!selectedPoint || !values?.order_locations) return 1; // Por defecto usar punto 1
    const index = values.order_locations.findIndex(
      (loc) =>
        Number(loc.latitude) === Number(selectedPoint.latitude) &&
        Number(loc.longitude) === Number(selectedPoint.longitude)
    );
    return index >= 0 ? index + 1 : 1; // +1 porque los marcadores empiezan en 1
  }, [selectedPoint, values?.order_locations]);

  const circleOptions = [
    { radius: 5000, color: "#00FF00", label: "5 km" },
    { radius: 10000, color: "#66B2FF", label: "10 km" },
    { radius: 15000, color: "#FF0000", label: "15 km" },
  ];

  // Generar colores únicos para cada orden
  const orderColors = useMemo(() => {
    const colors = [
      "#FFFF00", // Amarillo
      "#FF00FF", // Magenta
      "#00FF00", // Verde
      "#00FFFF", // Cyan
      "#FFA500", // Naranja
      "#FFC0CB", // Rosa
      "#FF0000", // Rojo
      "#66B2FF", // Azul
      "#A52A2A", // Marrón
      "#808080", // Gris
      "#800080", // Púrpura
      "#000080", // Azul marino
    ];
    const colorMap = {};
    nearbyPoints.forEach((order, index) => {
      colorMap[order.order_id] = colors[index % colors.length];
    });
    return colorMap;
  }, [nearbyPoints]);

  // Estado para almacenar iconos de marcadores procesados
  const [markerIcons, setMarkerIcons] = useState({});
  const [directionsPath, setDirectionsPath] = useState([]);

  // Obtener puntos a mostrar (filtrados por orden seleccionado)
  const pointsToShow = useMemo(() => {
    if (!selectedOrderId) {
      return nearbyPoints.flatMap((p) =>
        p.locations.map((l, i) => ({
          ...l,
          orderId: p.order_id,
          pointIndex: i + 1,
        }))
      );
    }
    const selectedOrder = nearbyPoints.find(
      (p) => p.order_id === selectedOrderId
    );
    if (!selectedOrder) return [];
    return selectedOrder.locations.map((l, i) => ({
      ...l,
      orderId: selectedOrder.order_id,
      pointIndex: i + 1,
    }));
  }, [nearbyPoints, selectedOrderId]);

  useEffect(() => {
    if (!selectedOrderId) {
      setDirectionsPath([]);
      return;
    }

    const selectedOrder = nearbyPoints.find(
      (p) => p.order_id === selectedOrderId
    );
    if (!selectedOrder?.locations || selectedOrder.locations.length < 2) {
      setDirectionsPath([]);
      return;
    }

    const cacheKey = `${selectedOrderId}-${selectedOrder.locations
      .map((loc) => `${loc.latitude}-${loc.longitude}`)
      .join("|")}`;

    if (directionsCache.current[cacheKey]) {
      setDirectionsPath(directionsCache.current[cacheKey]);
      return;
    }

    let isCancelled = false;

    const fetchRoute = async () => {
      try {
        const response = await axiosClient.post("/locations/route", {
          locations: selectedOrder.locations,
        });

        if (isCancelled) return;

        const routePath = response.data?.data || [];

        // Convertir a formato {lat, lng}
        const formattedPath = routePath.map((point) => ({
          lat: point.lat || point.latitude,
          lng: point.lng || point.longitude,
        }));

        directionsCache.current[cacheKey] = formattedPath;
        if (!isCancelled) {
          setDirectionsPath(formattedPath);
        }
      } catch (error) {
        console.error("Error fetching route:", error);
        if (!isCancelled) {
          setDirectionsPath([]);
        }
      }
    };

    fetchRoute();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrderId, nearbyPoints]);

  // Función para crear un icono con borde ajustado al contorno interior de la imagen
  const createMarkerIconWithBorder = useCallback((imageUrl, borderColor) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const borderWidth = 8; // Grosor del borde aumentado para mayor visibilidad
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Canvas con espacio para el borde
        canvas.width = img.width + borderWidth * 2;
        canvas.height = img.height + borderWidth * 2;

        // Método simplificado y eficiente para crear borde que sigue el contorno
        ctx.save();

        // Crear canvas para el borde
        const borderCanvas = document.createElement("canvas");
        borderCanvas.width = canvas.width;
        borderCanvas.height = canvas.height;
        const borderCtx = borderCanvas.getContext("2d");

        // Método mejorado: usar shadow blur para crear borde que sigue el contorno
        // Dibujar la imagen con sombra del color del borde (más intensa)
        borderCtx.shadowColor = borderColor;
        borderCtx.shadowBlur = borderWidth * 3; // Aumentado para borde más visible
        borderCtx.shadowOffsetX = 0;
        borderCtx.shadowOffsetY = 0;

        // Dibujar la imagen con sombra (esto crea el efecto de borde)
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Limpiar la sombra
        borderCtx.shadowBlur = 0;
        borderCtx.shadowColor = "transparent";

        // Crear máscara para que el borde solo aparezca fuera de la imagen
        // Dibujar la imagen original encima para "recortar" el borde interno
        borderCtx.globalCompositeOperation = "destination-over";
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Cambiar composición para dibujar la imagen original encima
        borderCtx.globalCompositeOperation = "source-over";
        borderCtx.drawImage(
          img,
          borderWidth,
          borderWidth,
          img.width,
          img.height
        );

        // Copiar el resultado al canvas principal
        ctx.drawImage(borderCanvas, 0, 0);

        ctx.restore();

        resolve(canvas.toDataURL());
      };

      img.onerror = () => resolve(imageUrl);
      img.src = imageUrl;
    });
  }, []);

  // Función helper para crear configuración de icono de forma segura
  const createIconConfig = useCallback((url, size, origin, anchor) => {
    if (window.google?.maps) {
      return {
        url,
        scaledSize: new window.google.maps.Size(size.width, size.height),
        origin: new window.google.maps.Point(origin.x, origin.y),
        anchor: new window.google.maps.Point(anchor.x, anchor.y),
      };
    }
    return {
      url,
      scaledSize: { width: size.width, height: size.height },
      origin: { x: origin.x, y: origin.y },
      anchor: { x: anchor.x, y: anchor.y },
    };
  }, []);

  // Crear iconos de marcadores con bordes cuando cambian los puntos
  useEffect(() => {
    if (!isGoogleMapsLoaded || pointsToShow.length === 0) return;

    const createIcons = async () => {
      const iconPromises = pointsToShow.map(async (point) => {
        const markerUrl = `/assets/img/varo/orders/markerLocation${point.pointIndex}.png`;
        const orderColor = orderColors[point.orderId] || "#000000";
        // Incluir el color en la clave para que se recree cuando cambie el color
        const iconKey = `${point.orderId}-${point.id}-${point.pointIndex}-${orderColor}`;

        if (!markerIcons[iconKey]) {
          const iconDataUrl = await createMarkerIconWithBorder(
            markerUrl,
            orderColor
          );
          return { key: iconKey, dataUrl: iconDataUrl };
        }
        return null;
      });

      const newIcons = await Promise.all(iconPromises);
      const iconsToAdd = newIcons.filter(Boolean).reduce((acc, icon) => {
        if (icon) acc[icon.key] = icon.dataUrl;
        return acc;
      }, {});

      if (Object.keys(iconsToAdd).length > 0) {
        setMarkerIcons((prev) => ({ ...prev, ...iconsToAdd }));
      }
    };

    createIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pointsToShow,
    orderColors,
    isGoogleMapsLoaded,
    createMarkerIconWithBorder,
  ]);

  return (
    <div className="modal fade" ref={modalOrderValues}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <div className="w-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="modal-title mb-0">
                  Mapa de ubicaciones cercanas
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => modalInstance && modalInstance.hide()}
                ></button>
              </div>
              <MapFilters
                values={values}
                filters={filters}
                setFilters={setFilters}
                onSearch={handleSearch}
              />
            </div>
          </div>

          <div className="modal-body" style={{ minHeight: "600px" }}>
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              onLoad={() => {
                // Callback cuando LoadScript ha cargado la librería
                try {
                  if (
                    typeof window !== "undefined" &&
                    window.google &&
                    typeof window.google === "object" &&
                    window.google.maps &&
                    typeof window.google.maps === "object"
                  ) {
                    setIsGoogleMapsLoaded(true);
                  }
                } catch (error) {
                  console.warn("Error in LoadScript onLoad:", error);
                }
              }}
              onError={() => {
                console.error("Error loading Google Maps API");
                setIsGoogleMapsLoaded(false);
              }}
            >
              {loading || !isGoogleMapsLoaded ? (
                <>
                  {/* <Loader isLoading={true} /> */}
                  <ModalSkeleton />
                </>
              ) : (
                <>
                  {/* Selector de puntos base en la parte superior */}
                  <div className="mb-3">
                    <BasePointSelector
                      locations={values?.order_locations || []}
                      selectedPoint={selectedPoint}
                      onSelectPoint={handleSelectPoint}
                    />
                  </div>
                  <Row>
                    {/* Panel lateral */}
                    <Col md={4}>
                      <div className="p-2 bg-light rounded shadow-sm">
                        <h6 className="fw-bold mb-2">Radio de búsqueda</h6>
                        <div className="d-flex gap-2 mb-3 flex-wrap">
                          {circleOptions.map((option) => {
                            const isSelected =
                              !showAllRadii && radius === option.radius;
                            const colorMap = {
                              5000: "#008000", // verde oscuro
                              10000: "#66B2FF", // azul
                              15000: "#FF0000", // rojo
                            };
                            const textColorMap = {
                              5000: "#006600", // verde oscuro para texto
                              10000: "#0000CC", // azul oscuro para texto
                              15000: "#CC0000", // rojo oscuro para texto
                            };
                            const btnColor = colorMap[option.radius];
                            const textColor = textColorMap[option.radius];

                            return (
                              <Button
                                key={option.radius}
                                onClick={() => {
                                  setShowAllRadii(false);
                                  setRadius(option.radius);
                                  // El useEffect se encargará de actualizar los puntos inmediatamente
                                }}
                                style={{
                                  backgroundColor: isSelected
                                    ? btnColor
                                    : "transparent",
                                  borderColor: btnColor,
                                  color: isSelected ? "#FFF" : textColor,
                                  borderWidth: isSelected ? "3px" : "2px",
                                  fontWeight: isSelected ? "bold" : "normal",
                                  boxShadow: isSelected
                                    ? `0 0 8px ${btnColor}`
                                    : "none",
                                  padding: "8px 16px",
                                  fontSize: "14px",
                                }}
                              >
                                {option.label}
                              </Button>
                            );
                          })}
                          <Button
                            onClick={() => {
                              setShowAllRadii(true);
                              // El useEffect se encargará de actualizar los puntos
                            }}
                            style={{
                              backgroundColor: showAllRadii
                                ? "#800080"
                                : "transparent",
                              borderColor: "#800080",
                              color: showAllRadii ? "#FFF" : "#800080",
                              borderWidth: showAllRadii ? "3px" : "2px",
                              fontWeight: showAllRadii ? "bold" : "normal",
                              boxShadow: showAllRadii
                                ? "0 0 8px #800080"
                                : "none",
                              padding: "8px 16px",
                              fontSize: "14px",
                            }}
                          >
                            Todos
                          </Button>
                        </div>

                        <NearbyOrdersList
                          nearbyPoints={nearbyPoints}
                          orderColors={orderColors}
                          selectedOrderId={selectedOrderId}
                          setSelectedOrderId={setSelectedOrderId}
                          selectedPoint={selectedPoint}
                        />
                      </div>
                    </Col>

                    {/* Mapa */}
                    <Col md={8}>
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "600px" }}
                        center={center}
                        zoom={selectedPoint ? 12 : 11}
                        onLoad={(map) => {
                          // Guardar la referencia del mapa
                          mapRef.current = map;
                          // Verificación adicional cuando el mapa está completamente cargado
                          // Esto asegura que todas las clases necesarias están disponibles
                          try {
                            if (
                              typeof window !== "undefined" &&
                              window.google &&
                              typeof window.google === "object" &&
                              window.google.maps &&
                              typeof window.google.maps === "object" &&
                              typeof window.google.maps.Size === "function" &&
                              typeof window.google.maps.Point === "function" &&
                              typeof window.google.maps.Marker === "function" &&
                              typeof window.google.maps.Circle === "function"
                            ) {
                              setIsGoogleMapsLoaded(true);
                            } else {
                              console.warn(
                                "Google Maps loaded but required classes are not available"
                              );
                            }
                          } catch (error) {
                            console.warn("Error in GoogleMap onLoad:", error);
                          }
                        }}
                      >
                        {selectedPoint && isGoogleMapsLoaded && (
                          <>
                            {/* Mostrar círculos según el modo seleccionado */}
                            {showAllRadii
                              ? // Mostrar los 3 círculos cuando está en modo "Todos"
                                circleOptions.map((c, i) => (
                                  <Circle
                                    key={i}
                                    center={center}
                                    radius={c.radius}
                                    options={{
                                      strokeColor: c.color,
                                      fillColor: c.color,
                                      fillOpacity: 0.2,
                                    }}
                                  />
                                ))
                              : // Mostrar solo el círculo del radio seleccionado
                                circleOptions
                                  .filter((c) => c.radius === radius)
                                  .map((c, i) => (
                                    <Circle
                                      key={i}
                                      center={center}
                                      radius={c.radius}
                                      options={{
                                        strokeColor: c.color,
                                        fillColor: c.color,
                                        fillOpacity: 0.2,
                                      }}
                                    />
                                  ))}

                            {/* Círculo de búsqueda extendida (100 km) - negro claro */}
                            {extendedSearch && extendedSearch.radius && (
                              <Circle
                                key="extended-search-circle"
                                center={center}
                                radius={extendedSearch.radius}
                                options={{
                                  strokeColor: "#4F4F4F", // Negro claro
                                  fillColor: "#4F4F4F", // Negro claro
                                  fillOpacity: 0.35, // Opacidad más baja para que sea más sutil
                                  strokeWeight: 2,
                                }}
                              />
                            )}

                            {/* Punto base */}
                            {isGoogleMapsLoaded && (
                              <Marker
                                position={center}
                                icon={createIconConfig(
                                  `/assets/img/varo/orders/markerTarifa${selectedPointIndex}.png`,
                                  { width: 60, height: 60 },
                                  { x: 0, y: 0 },
                                  { x: 30, y: 60 }
                                )}
                                options={{
                                  clickable: false,
                                  zIndex: 1000, // Asegurar que esté visualmente por delante
                                }}
                              />
                            )}

                            {/* Puntos cercanos */}
                            {isGoogleMapsLoaded &&
                              pointsToShow.map((point) => {
                                const markerUrl = `/assets/img/varo/orders/markerLocation${point.pointIndex}.png`;
                                const orderColor =
                                  orderColors[point.orderId] || "#000000";
                                // Incluir el color en la clave para que coincida con la creación del icono
                                const iconKey = `${point.orderId}-${point.id}-${point.pointIndex}-${orderColor}`;
                                const processedIconUrl =
                                  markerIcons[iconKey] || markerUrl;

                                const iconConfig = createIconConfig(
                                  processedIconUrl,
                                  { width: 60, height: 60 },
                                  { x: 0, y: 0 },
                                  { x: 30, y: 60 }
                                );

                                return (
                                  <Marker
                                    key={`${point.orderId}-${point.id}`}
                                    position={{
                                      lat: parseFloat(point.latitude),
                                      lng: parseFloat(point.longitude),
                                    }}
                                    icon={iconConfig}
                                    onClick={() => {
                                      // Si ya está seleccionada, deseleccionar; si no, seleccionar
                                      setSelectedOrderId(
                                        selectedOrderId === point.orderId
                                          ? null
                                          : point.orderId
                                      );
                                    }}
                                  />
                                );
                              })}

                            {isGoogleMapsLoaded &&
                              directionsPath.length > 1 && (
                                <Polyline
                                  path={directionsPath}
                                  options={{
                                    strokeColor: "#1a73e8",
                                    strokeOpacity: 0.9,
                                    strokeWeight: 5,
                                    geodesic: true,
                                  }}
                                />
                              )}
                          </>
                        )}
                      </GoogleMap>
                    </Col>
                  </Row>
                </>
              )}
            </LoadScript>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => modalInstance && modalInstance.hide()}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalOrdersMap;
